import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain } from 'utilities/helper/format'
import { setCommissionPlan } from 'services/plan'
import {
    Card,
    Notice,
    PrimaryButton,
    SecondaryButton,
    apiMessage,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashbox } from 'modules/cashbox/hooks'

/**
 * نسخه‌ی صندوق — commission vs subscription, for a cashbox.
 *
 * A cashbox created on the subscription plan is born in state `unp`
 * («در انتظار پرداخت اشتراک») and cannot be used until either an Order is paid
 * or the manager switches to the commission plan. Both routes out of that
 * state live here.
 *
 * `switch-to-commission` and `calculate-price-service-package` have always
 * accepted `box_type: 'csb'`; the PWA simply hardcoded `mnp`, which is why a
 * cashbox could reach `unp` and have nowhere to go.
 */
export default function CashboxPlansPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const finishFlow = useFinishFlow(FLOWS.cashboxPlan)
    const queryClient = useQueryClient()
    const { cashbox, isLoading, isError, refetch } = useCashbox(id)

    const toCommission = useMutation({
        mutationFn: () => setCommissionPlan(Number(id), 'csb'),
        onSuccess: () => {
            posthog.capture('cashbox_plan_switched', {
                cashbox_id: id,
                to_plan: 'cms',
            })
            toast.success('صندوق به نسخه‌ی کارمزدی تغییر کرد')
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['get-boxes'])
            finishFlow(`/cashbox/${id}`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_plan_switch_failed', {
                cashbox_id: id,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    // `cashbox_plan_switched` has never fired, and the code is fine — the
    // precondition is just rare (owner only, and only shown for a box already
    // on the subscription plan). Without an upstream event there is no way to
    // tell "nobody switches" from "nobody ever reaches this screen".
    const plan = cashbox?.plan
    const state = cashbox?.state
    const remainingDaysProp = cashbox?.service_package_remaining_days
    useEffect(() => {
        if (!cashbox) return
        posthog.capture('cashbox_plans_viewed', {
            cashbox_id: id,
            plan,
            state,
            is_unpaid: state === 'unp',
            remaining_days: remainingDaysProp,
        })
    }, [cashbox, id, plan, state, remainingDaysProp])

    if (isLoading) return <DotsLoading />
    if (isError || !cashbox)
        return (
            <div className="bg-white min-h-screen">
                <Header title="نسخه‌ی صندوق" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="نسخه‌ی صندوق" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند نسخه‌ی صندوق را تغییر دهد.
                    </Notice>
                </div>
            </div>
        )

    const isUnpaid = cashbox.state === 'unp'
    const isSubscription = cashbox.plan === 'sub'
    const remainingDays = cashbox.service_package_remaining_days

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="نسخه‌ی صندوق" />
            </div>

            <div className="px-4 pt-3">
                {isUnpaid && (
                    <div className="mb-3">
                        <Notice tone="warn">
                            این صندوق تا زمانی که نسخه‌اش مشخص نشود قابل
                            استفاده نیست. یکی از دو نسخه‌ی زیر را انتخاب کنید.
                        </Notice>
                    </div>
                )}

                <Card className="mb-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            نسخه‌ی فعلی
                        </span>
                        <span className="text-sm font-bold text-black">
                            {isSubscription ? 'اشتراکی' : 'کارمزدی'}
                        </span>
                    </div>
                    {isSubscription &&
                        typeof remainingDays === 'number' &&
                        !isUnpaid && (
                            <div className="flex justify-between items-baseline mt-2">
                                <span className="text-sm text-text-secondary">
                                    اعتبار باقی‌مانده
                                </span>
                                <span className="text-sm text-black">
                                    {remainingDays > 0
                                        ? `${faPlain.format(
                                              remainingDays
                                          )} روز`
                                        : 'منقضی شده'}
                                </span>
                            </div>
                        )}
                </Card>

                {/* subscription --------------------------------------- */}
                <Card className="mb-3">
                    <p className="text-base font-bold text-black">
                        نسخه‌ی اشتراکی
                    </p>
                    <p className="text-sm text-text-secondary mt-1 leading-6">
                        مبلغی ماهانه (یا سه‌ماهه) بابت خودِ صندوق پرداخت
                        می‌کنید و از اعضا بابت پرداخت سهم کارمزدی گرفته
                        نمی‌شود.
                    </p>
                    <div className="mt-3">
                        <PrimaryButton
                            onClick={() =>
                                navigate(`/cashbox/${id}/plans/subscription`)
                            }
                        >
                            {isSubscription && !isUnpaid
                                ? 'تمدید اشتراک'
                                : 'خرید اشتراک'}
                        </PrimaryButton>
                    </div>
                </Card>

                {/* commission ----------------------------------------- */}
                <Card>
                    <p className="text-base font-bold text-black">
                        نسخه‌ی کارمزدی
                    </p>
                    <p className="text-sm text-text-secondary mt-1 leading-6">
                        بابت خودِ صندوق چیزی نمی‌پردازید؛ به‌جای آن، هنگام
                        پرداخت آنلاین سهم، از هر عضو کارمزد گرفته می‌شود.
                    </p>
                    {!isSubscription ? (
                        <p className="text-xs text-[#4CA632] mt-3">
                            صندوق هم‌اکنون روی همین نسخه است.
                        </p>
                    ) : (
                        <div className="mt-3">
                            <SecondaryButton
                                disabled={toCommission.isLoading}
                                onClick={() => {
                                    if (
                                        !window.confirm(
                                            'صندوق به نسخه‌ی کارمزدی تغییر کند؟ اشتراک باقی‌مانده بازگردانده نمی‌شود.'
                                        )
                                    )
                                        return
                                    posthog.capture(
                                        'cashbox_plan_switch_started',
                                        { cashbox_id: id, to_plan: 'cms' }
                                    )
                                    toCommission.mutate()
                                }}
                            >
                                {toCommission.isLoading
                                    ? 'در حال تغییر…'
                                    : 'تغییر به نسخه‌ی کارمزدی'}
                            </SecondaryButton>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    )
}
